/**
 * API layer for i18n dashboard feature.
 */

import { apiClient } from '@/infrastructure/api/client';
import type {
  DashboardStats,
  DashboardQueues,
  DashboardCharts,
  ActivityFeed,
  MyActivity,
  DeprecatedKeysInDatabase,
  DeprecatedKeyRecords,
  KeyReportExecution,
  KeyReportHistoryResponse,
  LatestKeyReportResponse,
  KeyReportKeysResponse,
} from './types';

const BASE_PATH = '/v1.0/i18n/dashboard';

export async function fetchDashboardStats(): Promise<DashboardStats> {
  const response = await apiClient.get<DashboardStats>(BASE_PATH);
  return response.data;
}

export async function fetchDashboardQueues(): Promise<DashboardQueues> {
  const response = await apiClient.get<DashboardQueues>(`${BASE_PATH}/queues`);
  return response.data;
}

export async function fetchDashboardCharts(): Promise<DashboardCharts> {
  const response = await apiClient.get<DashboardCharts>(`${BASE_PATH}/charts`);
  return response.data;
}

export async function fetchActivityFeed(limit = 10): Promise<ActivityFeed> {
  const response = await apiClient.get<ActivityFeed>(`${BASE_PATH}/activity`, { limit });
  return response.data;
}

export async function fetchMyActivity(): Promise<MyActivity> {
  const response = await apiClient.get<MyActivity>(`${BASE_PATH}/my-activity`);
  return response.data;
}

export async function fetchDeprecatedKeysInDatabase(): Promise<DeprecatedKeysInDatabase> {
  const response = await apiClient.get<DeprecatedKeysInDatabase>(`${BASE_PATH}/deprecated-in-db`);
  return response.data;
}

export async function fetchDeprecatedKeyRecords(
  tableName: string,
  columnName: string,
  page = 1,
  pageSize = 20
): Promise<DeprecatedKeyRecords> {
  const response = await apiClient.get<DeprecatedKeyRecords>(
    `${BASE_PATH}/deprecated-in-db/${encodeURIComponent(tableName)}/${encodeURIComponent(columnName)}`,
    { page, pageSize }
  );
  return response.data;
}

// ---------- Key Report ----------

export async function fetchLatestKeyReportExecution(
  status?: string,
  branch?: string
): Promise<KeyReportExecution | null> {
  const params: Record<string, string> = {};
  if (status) {
    params.status = status;
  }
  if (branch) {
    params.branch = branch;
  }
  const response = await apiClient.get<LatestKeyReportResponse>(
    `${BASE_PATH}/key-report/latest`,
    params
  );
  return response.data.execution;
}

export async function fetchKeyReportHistory(
  page = 1,
  pageSize = 10
): Promise<KeyReportHistoryResponse> {
  const response = await apiClient.get<KeyReportHistoryResponse>(
    `${BASE_PATH}/key-report/history`,
    { page, pageSize }
  );
  return response.data;
}

export async function triggerKeyReport(
  targetBranch?: string
): Promise<{ executionUuid: string }> {
  const response = await apiClient.post<{ executionUuid: string }>(
    `${BASE_PATH}/key-report/trigger`,
    targetBranch ? { targetBranch } : {}
  );
  return response.data;
}

export async function fetchKeyReportKeys(
  executionUuid: string,
  category: string,
  page = 1,
  pageSize = 20
): Promise<KeyReportKeysResponse> {
  const response = await apiClient.get<KeyReportKeysResponse>(
    `${BASE_PATH}/key-report/${executionUuid}/keys`,
    { category, page, pageSize }
  );
  return response.data;
}
